import { useCallback, useEffect, useRef, useState } from 'react'

import type { GlossaryTerm } from '../glossary.interface'

const TOPBAR_HEIGHT = 80

function getScrollContainer(): HTMLElement | null {
  return document.querySelector<HTMLElement>('.main-content')
}

export function useActiveGlossaryTerm(terms: GlossaryTerm[]): {
  activeTermId: string
  scrollToTerm: (termId: string) => void
} {
  const [activeTermId, setActiveTermId] = useState(terms[0]?.id ?? '')
  const isScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const rafRef = useRef<number | undefined>(undefined)

  const scrollToTerm = useCallback((termId: string) => {
    const element = document.getElementById(termId)
    const scrollContainer = getScrollContainer()
    if (!element || !scrollContainer) return

    isScrollingRef.current = true
    setActiveTermId(termId)

    const containerRect = scrollContainer.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()
    const offset = elementRect.top - containerRect.top + scrollContainer.scrollTop - TOPBAR_HEIGHT

    scrollContainer.scrollTo({ top: offset, behavior: 'smooth' })

    clearTimeout(scrollTimeoutRef.current)
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false
    }, 800)
  }, [])

  useEffect(() => {
    const scrollContainer = getScrollContainer()
    if (!scrollContainer) return

    const termIds = terms.map((t) => t.id)

    function updateActiveOnScroll() {
      if (isScrollingRef.current) return

      const containerTop = scrollContainer!.getBoundingClientRect().top + TOPBAR_HEIGHT
      let closestId = termIds[0]
      let closestDistance = Infinity

      for (const id of termIds) {
        const el = document.getElementById(id)
        if (!el) continue

        const rect = el.getBoundingClientRect()
        const distance = rect.top - containerTop

        if (distance <= 20 && Math.abs(distance) < closestDistance) {
          closestDistance = Math.abs(distance)
          closestId = id
        }
      }

      if (closestId) {
        setActiveTermId(closestId)
      }
    }

    function onScroll() {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(updateActiveOnScroll)
    }

    scrollContainer.addEventListener('scroll', onScroll, { passive: true })
    updateActiveOnScroll()

    return () => {
      scrollContainer.removeEventListener('scroll', onScroll)
      clearTimeout(scrollTimeoutRef.current)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [terms])

  return { activeTermId, scrollToTerm }
}
